// 2026-05-26 - FormuLearning - Step 6 API 호출
// 2026-09-24 수정 - 이슈 #63: PK 재설계 — 코호트·실측 업로드 폐지, 입력 4군·배치 후보·문헌 검색·내보내기
//   (개정안_260924_Step6 PK 재설계 §5)
// 파일 위치: frontend/src/api/step6.ts

import client from './client'
import type { ApiResponse } from '../types/api'

export type ProfileRow = { ph: number; points: [number, number | null][] }
export type DissBlock = { lot_id?: string; batch_id?: string; label?: string; profiles: ProfileRow[] } | null

export interface PkInputs {
  drug: { api_name?: string | null; dose_mg?: number | null; half_life_h?: number | null; vd_l?: number | null }
  bio: { mode: 'ref' | 'unk'; F?: number | null; qh?: number | null; fu?: number | null; clint?: number | null; basis: 'whole' | 'hep' }
  absorption: { method: 'Caco-2' | 'PAMPA'; papp_cm_s?: number | null; upper_h?: number | null; lower_h?: number | null }
  solubility: { ph: number; mg_ml: number | null }[]
  dissolution: { ref: DissBlock; test: DissBlock }
  advanced: Record<string, number>
  sim_time_h: number
  linked?: Record<string, { value: unknown; source: string }>
}

export interface LinkedItem {
  value: unknown
  source: string
  options?: string[]
}

export interface FormResult {
  metrics: {
    cmax: number; tmax: number; auc: number; fa: number; fg: number; fh: number; f: number
    upper: number; lower: number; fpl: number; mass: number; massErr: number
  }
  fit: { pH: number; F_inf: number; alpha_h: number; beta: number; R2: number }[]
  pclass: string
  peu: number
  pel: number
  kau: number
  kal: number
  curve: { time_h: number[]; conc_ng_mL: number[] }
}

export interface SimulationResult {
  engine?: { name: string; source: string; version: string }
  inputs_hash?: string
  sim_time_h: number
  ref: FormResult
  test: FormResult
  ratio: { cmax_pct: number | null; auc_pct: number | null; tmax_diff_h: number }
  bio_mode: 'ref' | 'unk'
  bio_extra: Record<string, number | boolean>
  run_at: string
}

export interface Step6View {
  id: string
  project_id: string
  inputs: PkInputs
  is_saved: boolean
  linked: Record<string, LinkedItem>
  link_groups: Record<string, string[]>
  stale: string[]
  stale_groups: string[]
  simulation_result: SimulationResult | Record<string, never>
  result_outdated: boolean
  ai_summary: string | null
  ai_summary_ko: string | null
  updated_at: string | null
}

export interface BatchCandidates {
  reference: { lot_id: string; label: string; is_active: boolean; profiles: ProfileRow[] }[]
  test: { batch_id: string; batch_code: string; round_no: number | null; is_final: boolean; profiles: ProfileRow[] }[]
}

export interface PkSearchResult {
  ok: boolean
  message?: string
  api_name?: string
  half_life_h?: number | null
  vd_l?: number | null
  vd_l_per_kg?: number | null
  bioavailability?: number | null
  fu?: number | null
  sources?: string[]
}

export async function getStep6(projectId: string): Promise<Step6View> {
  const { data } = await client.get<ApiResponse<Step6View>>(`/step6/${projectId}/`)
  return data.data!
}

export async function updateStep6(projectId: string, inputs: PkInputs): Promise<Step6View> {
  const { data } = await client.put<ApiResponse<Step6View>>(`/step6/${projectId}/`, { inputs })
  return data.data!
}

export async function getBatchCandidates(projectId: string): Promise<BatchCandidates> {
  const { data } = await client.get<ApiResponse<BatchCandidates>>(`/step6/${projectId}/batches`)
  return data.data!
}

export async function runSimulation(projectId: string, inputs: PkInputs): Promise<Step6View> {
  const { data } = await client.post<ApiResponse<Step6View>>(`/step6/${projectId}/simulate`, { inputs })
  return data.data!
}

export async function searchPkParams(projectId: string, apiName: string): Promise<PkSearchResult> {
  const { data } = await client.post<ApiResponse<PkSearchResult>>(`/step6/${projectId}/pk-params/search`, { api_name: apiName })
  return data.data!
}

export async function downloadExport(projectId: string, kind: 'simulation' | 'summary' | 'weibull'): Promise<void> {
  const res = await client.get(`/step6/${projectId}/export/${kind}`, { responseType: 'blob' })
  const name = /filename="([^"]+)"/.exec(res.headers['content-disposition'] ?? '')?.[1] ?? `step6_${kind}.csv`
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}

export async function generateAiSummary(projectId: string): Promise<{ ai_summary: string; ai_summary_ko: string | null }> {
  const { data } = await client.post<ApiResponse<{ ai_summary: string; ai_summary_ko: string | null }>>(`/step6/${projectId}/ai-summary`)
  return data.data!
}

/** axios 오류에서 서버 사유(detail)를 꺼낸다. */
export function errorDetail(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof d === 'string') return d
  return e instanceof Error ? e.message : '요청이 실패했습니다.'
}
